// src/index.ts
import { unzipSync } from "fflate";
var CELL_WIDTH = 192;
var CELL_HEIGHT = 208;
var ATLAS_COLUMNS = 8;
var V2_ROWS = 11;
var V2_WIDTH = CELL_WIDTH * ATLAS_COLUMNS;
var V2_HEIGHT = CELL_HEIGHT * V2_ROWS;
var repeatWithFinal = (count, duration, finalDuration) => [
  ...Array.from({ length: count - 1 }, () => duration),
  finalDuration
];
var STANDARD_STATE_SPECS = [
  { key: "idle", label: "Idle", row: 0, frameCount: 6, durations: [280, 110, 110, 140, 140, 320] },
  { key: "running-right", label: "Run right", row: 1, frameCount: 8, durations: repeatWithFinal(8, 120, 220) },
  { key: "running-left", label: "Run left", row: 2, frameCount: 8, durations: repeatWithFinal(8, 120, 220) },
  { key: "waving", label: "Waving", row: 3, frameCount: 4, durations: repeatWithFinal(4, 140, 280) },
  { key: "jumping", label: "Jumping", row: 4, frameCount: 5, durations: repeatWithFinal(5, 140, 280) },
  { key: "failed", label: "Failed", row: 5, frameCount: 8, durations: repeatWithFinal(8, 140, 240) },
  { key: "waiting", label: "Waiting", row: 6, frameCount: 6, durations: repeatWithFinal(6, 150, 260) },
  { key: "running", label: "Running", row: 7, frameCount: 6, durations: repeatWithFinal(6, 120, 220) },
  { key: "review", label: "Review", row: 8, frameCount: 6, durations: repeatWithFinal(6, 150, 280) }
];
var LOOK_DIRECTIONS = [0, 22.5, 45, 67.5, 90, 112.5, 135, 157.5, 180, 202.5, 225, 247.5, 270, 292.5, 315, 337.5];
function getStateSpec(state) {
  const spec = STANDARD_STATE_SPECS.find((item) => item.key === state);
  if (!spec) throw new Error(`Unknown pet state: ${state}`);
  return spec;
}
function getFrameLocation(state, frame) {
  const spec = getStateSpec(state);
  const column = Math.min(Math.max(0, frame), spec.frameCount - 1);
  return {
    row: spec.row,
    column,
    x: column * CELL_WIDTH,
    y: spec.row * CELL_HEIGHT,
    width: CELL_WIDTH,
    height: CELL_HEIGHT
  };
}
function getDirectionLocation(degrees) {
  const normalized = (degrees % 360 + 360) % 360;
  const index = Math.round(normalized / 22.5) % 16;
  const row = index < 8 ? 9 : 10;
  const column = index % 8;
  return {
    degrees: LOOK_DIRECTIONS[index],
    index,
    row,
    column,
    x: column * CELL_WIDTH,
    y: row * CELL_HEIGHT,
    width: CELL_WIDTH,
    height: CELL_HEIGHT
  };
}
function getDirectionFromVector(dx, dy, deadzone = 12) {
  if (Math.hypot(dx, dy) < deadzone) return null;
  const degrees = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
  return getDirectionLocation(degrees);
}
function getFrameAtElapsed(state, elapsedMs) {
  const spec = getStateSpec(state);
  const cycle = spec.durations.reduce((sum, duration) => sum + duration, 0);
  let cursor = (elapsedMs % cycle + cycle) % cycle;
  for (let frame = 0; frame < spec.durations.length; frame += 1) {
    if (cursor < spec.durations[frame]) return frame;
    cursor -= spec.durations[frame];
  }
  return 0;
}
function expectedAtlasDimensions() {
  return { width: V2_WIDTH, height: V2_HEIGHT };
}
function validateManifest(value) {
  const issues = [];
  if (!value || typeof value !== "object") return { issues: [{ code: "manifest.invalid", message: "pet.json must contain an object", severity: "error" }] };
  const manifest = value;
  if (!manifest.id || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(manifest.id)) issues.push({ code: "manifest.id", message: "id must be a lowercase slug", severity: "error" });
  if (!manifest.displayName?.trim()) issues.push({ code: "manifest.displayName", message: "displayName is required", severity: "error" });
  if (!manifest.description?.trim()) issues.push({ code: "manifest.description", message: "description is required", severity: "error" });
  if (manifest.spritesheetPath !== "spritesheet.webp") issues.push({ code: "manifest.spritesheetPath", message: "spritesheetPath must be spritesheet.webp", severity: "error" });
  if (manifest.spriteVersionNumber !== 2) issues.push({ code: "manifest.version", message: "Unsupported legacy pet format. Codex Pet Lab requires spriteVersionNumber: 2", severity: "error" });
  return { manifest, issues };
}
function readAscii(bytes, start, length) {
  return String.fromCharCode(...bytes.slice(start, start + length));
}
function readUint24LE(bytes, offset) {
  return bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16;
}
function readWebPDimensions(bytes) {
  if (bytes.length < 30 || readAscii(bytes, 0, 4) !== "RIFF" || readAscii(bytes, 8, 4) !== "WEBP") throw new Error("spritesheet.webp is not a valid WebP container");
  const chunk = readAscii(bytes, 12, 4);
  if (chunk === "VP8X") return { width: readUint24LE(bytes, 24) + 1, height: readUint24LE(bytes, 27) + 1 };
  if (chunk === "VP8L") {
    if (bytes[20] !== 47) throw new Error("invalid lossless WebP signature");
    return {
      width: 1 + bytes[21] + ((bytes[22] & 63) << 8),
      height: 1 + (bytes[22] >> 6) + (bytes[23] << 2) + ((bytes[24] & 15) << 10)
    };
  }
  if (chunk === "VP8 ") {
    for (let offset = 20; offset + 9 < bytes.length; offset += 1) {
      if (bytes[offset] === 157 && bytes[offset + 1] === 1 && bytes[offset + 2] === 42) {
        return {
          width: (bytes[offset + 3] | bytes[offset + 4] << 8) & 16383,
          height: (bytes[offset + 5] | bytes[offset + 6] << 8) & 16383
        };
      }
    }
  }
  throw new Error(`unsupported WebP chunk ${chunk}`);
}
function validatePetPackage(bytes) {
  const issues = [];
  let entries;
  try {
    entries = unzipSync(bytes);
  } catch {
    return { ok: false, files: [], issues: [{ code: "zip.invalid", message: "file is not a readable ZIP archive", severity: "error" }], spritesheet: null };
  }
  const files = Object.keys(entries).sort();
  const manifestBytes = entries["pet.json"];
  const spritesheet = entries["spritesheet.webp"] ?? null;
  if (!manifestBytes) issues.push({ code: "zip.petJson", message: "archive must include pet.json at the root", severity: "error" });
  if (!spritesheet) issues.push({ code: "zip.spritesheet", message: "archive must include spritesheet.webp at the root", severity: "error" });
  let manifest;
  if (manifestBytes) {
    try {
      const parsed = JSON.parse(new TextDecoder().decode(manifestBytes));
      const result = validateManifest(parsed);
      manifest = result.manifest;
      issues.push(...result.issues);
    } catch {
      issues.push({ code: "manifest.json", message: "pet.json is not valid JSON", severity: "error" });
    }
  }
  let dimensions;
  if (spritesheet) {
    try {
      dimensions = readWebPDimensions(spritesheet);
      if (manifest) {
        const expected = expectedAtlasDimensions();
        if (dimensions.width !== expected.width || dimensions.height !== expected.height) issues.push({ code: "spritesheet.dimensions", message: `expected ${expected.width}x${expected.height}, got ${dimensions.width}x${dimensions.height}`, severity: "error" });
      }
    } catch (error) {
      issues.push({ code: "spritesheet.webp", message: error instanceof Error ? error.message : "invalid WebP spritesheet", severity: "error" });
    }
  }
  issues.push({ code: "visual.qa", message: "Structural validation does not replace Hatch Pet contact-sheet, motion, chroma, and direction QA", severity: "warning" });
  return { ok: !issues.some((issue) => issue.severity === "error"), manifest, dimensions, files, issues, spritesheet };
}
export {
  ATLAS_COLUMNS,
  CELL_HEIGHT,
  CELL_WIDTH,
  LOOK_DIRECTIONS,
  STANDARD_STATE_SPECS,
  V2_HEIGHT,
  V2_ROWS,
  V2_WIDTH,
  expectedAtlasDimensions,
  getDirectionFromVector,
  getDirectionLocation,
  getFrameAtElapsed,
  getFrameLocation,
  getStateSpec,
  readWebPDimensions,
  validateManifest,
  validatePetPackage
};
